"use client";
import { useCallback, useEffect, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { NodeSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"

// --- Lib ---
import { isExtensionAvailable } from "@/tiptap/lib/tiptap-utils"

// --- Icons ---
import { AlignCenterVerticalIcon } from "@/tiptap/icons/align-center-vertical-icon"
import { AlignEndVerticalIcon } from "@/tiptap/icons/align-end-vertical-icon"
import { AlignStartVerticalIcon } from "@/tiptap/icons/align-start-vertical-icon";

export const IMAGE_ALIGN_SHORTCUT_KEYS = {
  left: "alt+shift+l",
  center: "alt+shift+e",
  right: "alt+shift+r",
}

export const imageAlignIcons = {
  left: AlignStartVerticalIcon,
  center: AlignCenterVerticalIcon,
  right: AlignEndVerticalIcon,
}

export const imageAlignLabels = {
  left: "Image align left",
  center: "Image align center",
  right: "Image align right",
}

/**
 * Checks if image alignment can be performed in the current editor state
 */
export function canSetImageAlign(editor, align, extensionName = "image", attributeName = "data-align") {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, [extensionName])) return false

  return editor
    .can()
    .updateAttributes(extensionName, { [attributeName]: align });
}

/**
 * Checks if the image alignment is currently active
 */
export function isImageAlignActive(editor, align, extensionName = "image", attributeName = "data-align") {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, [extensionName])) return false

  const attributes = editor.getAttributes(extensionName)
  const currentAlign = attributes[attributeName] || "left"
  return currentAlign === align
}

/**
 * Sets image alignment in the editor
 */
export function setImageAlign(editor, align, extensionName = "image", attributeName = "data-align") {
  if (!editor?.isEditable) {
    return false
  }

  if (!isExtensionAvailable(editor, [extensionName])) {
    return false
  }

  if (!canSetImageAlign(editor, align, extensionName, attributeName)) {
    return false
  }

  try {
    const { selection } = editor.state
    const isNodeSelection = selection instanceof NodeSelection
    const selectionPosition = isNodeSelection
      ? selection.from
      : selection.$anchor.pos

    const alignmentUpdated = editor
      .chain()
      .focus()
      .updateAttributes(extensionName, { [attributeName]: align })
      .run()

    // Restore node selection if it was originally selected
    // This is the temporary solution as image-node-extension.ts contain content: "inline*"
    if (alignmentUpdated && isNodeSelection) {
      editor.commands.setNodeSelection(selectionPosition)
    }

    return alignmentUpdated
  } catch {
    return false
  }
}

/**
 * Determines if the image align button should be shown
 */
export function shouldShowButton(props) {
  const {
    editor,
    hideWhenUnavailable,
    align,
    extensionName = "image",
    attributeName = "data-align",
  } = props

  if (!editor || !editor.isEditable) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!isExtensionAvailable(editor, [extensionName])) return false

  return canSetImageAlign(editor, align, extensionName, attributeName);
}

/**
 * Custom hook that provides image align functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage with default "image" extension
 * function MySimpleImageAlignButton() {
 *   const { isVisible, handleImageAlign } = useImageAlign({ align: "center" })
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleImageAlign}>Align Center</button>
 * }
 *
 * // Advanced usage with custom extension name
 * function MyAdvancedImageAlignButton() {
 *   const { isVisible, handleImageAlign, label, isActive } = useImageAlign({
 *     editor: myEditor,
 *     align: "right",
 *     extensionName: "myCustomImage", // Use your custom extension name
 *     hideWhenUnavailable: true,
 *     onAligned: () => console.log('Image aligned!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleImageAlign}
 *       aria-pressed={isActive}
 *       aria-label={label}
 *     >
 *       Align Right
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useImageAlign(config) {
  const {
    editor: providedEditor,
    align,
    extensionName = "image",
    attributeName = "data-align",
    hideWhenUnavailable = false,
    onAligned,
  } = config

  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsBreakpoint()
  const [isVisible, setIsVisible] = useState(true)
  const canAlign = canSetImageAlign(editor, align, extensionName, attributeName)
  const isActive = isImageAlignActive(editor, align, extensionName, attributeName)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({
        editor,
        align,
        hideWhenUnavailable,
        extensionName,
        attributeName,
      }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)
    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable, align, extensionName, attributeName])

  const handleImageAlign = useCallback(() => {
    if (!editor) return false

    const success = setImageAlign(editor, align, extensionName, attributeName)
    if (success) {
      onAligned?.()
    }
    return success
  }, [editor, align, extensionName, attributeName, onAligned])

  useHotkeys(IMAGE_ALIGN_SHORTCUT_KEYS[align], (event) => {
    event.preventDefault()
    handleImageAlign()
  }, {
    enabled: isVisible && canAlign,
    enableOnContentEditable: !isMobile,
    enableOnFormTags: true,
  })

  return {
    isVisible,
    isActive,
    handleImageAlign,
    canAlign,
    label: imageAlignLabels[align],
    shortcutKeys: IMAGE_ALIGN_SHORTCUT_KEYS[align],
    Icon: imageAlignIcons[align],
  }
}
